import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Paperclip,
  Workflow,
  ShieldCheck,
  Cloud,
  Boxes,
  Route,
  Users,
  ListChecks,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: Paperclip,
    title: 'EBS feature that attaches documents to transactions',
    detail:
      'Managed Attachments is an Oracle E-Business Suite feature that lets users attach supporting documents — invoice PDFs, contracts, receivers, statements, sourcing artifacts — to EBS records (AP invoices, PO headers, AR receipts, projects, sourcing events) directly from EBS Forms and OAF screens.',
  },
  {
    icon: Boxes,
    title: 'WebCenter Content is the document store underneath',
    detail:
      "Managed Attachments doesn't store the actual file in the EBS database. The repository is Oracle WebCenter Content (UCM). EBS holds a pointer record; WCC holds the document, its metadata, security profile, and retention policy.",
  },
  {
    icon: Workflow,
    title: 'AME framework routes documents to the right folder',
    detail:
      'The Oracle Approvals Management Engine (AME) is the rules engine that determines which WCC folder, security group, and document profile each attachment lands in based on EBS context — operating unit, document type, supplier, business unit, project.',
  },
  {
    icon: ShieldCheck,
    title: 'Used across many EBS modules, not just AP',
    detail:
      'Most EBS shops that adopted Managed Attachments use it across Payables, Receivables, Sourcing, Projects, Purchasing, Lease and Finance Management, and Service Contracts. The same WCC tier underneath serves all of them.',
  },
];

const SCENARIOS = [
  {
    icon: Boxes,
    title: 'Managed Attachments deployed across many EBS modules',
    body: 'AP started it, then Receivables, Sourcing, Projects, and Purchasing all turned it on. The single WCC tier underneath now holds attachments for half a dozen EBS modules, each with its own folder structure, security profile, and retention rule. Replacing it is not a "swap the AP layer" conversation.',
  },
  {
    icon: Route,
    title: 'AME routing rules customized over years',
    body: 'The AME rule library has accumulated over a decade — operating-unit-specific routing, supplier-segment branching, project-classification folders, country-specific retention. The rule library is the institutional knowledge. Nobody wants to re-derive it from scratch.',
  },
  {
    icon: Cloud,
    title: 'Compliance wants attachments off on-prem WCC into OCI',
    body: 'Infosec or compliance has a directive to move document repositories off on-prem storage into OCI Object Storage. The EBS team is asked to do this without disturbing the EBS user experience, without breaking the AME routing rules, and without an EBS upgrade dependency.',
  },
  {
    icon: Workflow,
    title: 'EBS upgrade in flight — Managed Attachments compatibility is a question',
    body: 'The EBS upgrade plan (R12.1 → R12.2, R12.2 → R13 / Fusion, RPC patching) hits Managed Attachments compatibility as an explicit checklist item. The team needs to know what the Managed Attachments forward path looks like in parallel with the EBS upgrade itself.',
  },
];

const FORWARD_PATHS = [
  {
    label: 'Path 1',
    title: 'Upgrade WebCenter Content to 14c, keep Managed Attachments unchanged',
    body: 'WCC 14c (release 14.1.2) remains the document repository underneath EBS. The Managed Attachments configuration, AME routing rules, document profiles, and security model carry forward without functional change. EBS users see the same attach / view / search experience. Buys approximately 8 years of supported runway (Premier through Dec 2030, Extended through Dec 2033).',
    linkLabel: 'See the WebCenter 14c upgrade detail',
    linkTo: '/solutions/webcenter-14c-upgrade',
  },
  {
    label: 'Path 2',
    title: 'Migrate WCC file store to OCI Object Storage (preserve Managed Attachments UX)',
    body: 'The WCC application tier stays in place; the file store moves to OCI Object Storage via the Object Storage Migration Tool, which is built on top of the File Store Provider foundation. EBS Managed Attachments behaves identically — same browser-based attach screen, same AME routing, same security profile — but storage cost and durability shift to the OCI Object Storage tier.',
    linkLabel: null,
    linkTo: null,
  },
  {
    label: 'Path 3',
    title: 'Modernize the attachment tier to a cloud-native store',
    body: 'Replace the WCC repository with a cloud-native attachment store while preserving the EBS Managed Attachments user interface, the AME routing framework, document classification, and the integration contract EBS expects. Users do not retrain. The Open Interface and Forms-based attach screen remain the contract surface; what changes is what sits underneath.',
    linkLabel: null,
    linkTo: null,
  },
];

const PRESERVED = [
  'The EBS Managed Attachments user interface — Forms and OAF attach screens',
  'AME routing rules — operating unit, supplier, project, business unit',
  'Document classification — folders, security groups, document profiles',
  'Retention policy — per-folder and per-document-class retention windows',
  'Audit trail — who attached, when, against which EBS record',
  'The Payables Open Interface contract between EBS and the attachment tier',
];

const ENGAGEMENT_TIERS = [
  {
    label: 'Tier 1 · Managed Attachments Inventory',
    title: 'Fixed-fee read of your current EBS + WCC + AME footprint',
    body: 'Andrew reads the EBS Managed Attachments configuration, the WCC repository, and the AME rule library directly. Output: a structured inventory of every EBS module on Managed Attachments, every AME routing rule, every WCC document profile and folder, every security group, every retention rule. Yours to keep regardless of which forward path you choose.',
  },
  {
    label: 'Tier 2 · OCI Object Storage Migration (Standard Consulting)',
    title: 'File-store migration to OCI Object Storage',
    body: 'Project-based engagement to migrate the WCC file store to OCI Object Storage using the Object Storage Migration Tool. Scoped to a defined outcome: source file inventory, metadata preservation, migration window planning, parallel validation, cutover, and post-migration verification. EBS Managed Attachments behavior preserved end-to-end.',
  },
  {
    label: 'Tier 3 · Strategic Advisory Retainer',
    title: 'Founder-level guidance through the Managed Attachments decision',
    body: 'Monthly retainer with Andrew for EBS admins working the Managed Attachments forward-path decision in parallel with an EBS upgrade or a compliance directive. Strategic guidance on path tradeoffs, AME-rule migration implications, security-model preservation, and sequencing with the EBS upgrade plan.',
  },
  {
    label: 'Tier 4 · Forward Deployed Engineering',
    title: 'Custom AME archeology and custom Java integration',
    body: 'For implementations where AME has accumulated a decade of customer-specific routing logic and the integration into WCC includes custom Java services, an FDE engagement embeds Andrew directly. Forward Deployed Engineering work covers AME rule reverse engineering, custom Java service re-implementation, and integration contract preservation across the cutover.',
  },
];

const FAQS = [
  {
    q: 'Is Oracle EBS Managed Attachments being killed?',
    a: 'No — Managed Attachments is an EBS feature and is not being deprecated. What is changing is the support window for the WebCenter Content layer underneath it. Oracle Fusion Middleware 12c — which includes WebCenter Content — exits Premier Support in December 2026 and Extended Support in December 2027. The EBS Managed Attachments feature itself continues; the storage tier underneath needs a forward path.',
  },
  {
    q: 'What is the Fusion equivalent of EBS Managed Attachments?',
    a: 'Oracle Fusion Cloud ERP uses a different attachment model — typically Oracle Content Management (OCM) or the Universal Content Management embedded within Fusion — rather than the EBS Managed Attachments framework. Customers migrating EBS to Fusion treat attachments as a separate migration workstream because the configuration model, security model, and integration pattern are different. The EBS-on-Managed-Attachments forward path and the Fusion-attachments forward path are typically scoped independently.',
  },
  {
    q: 'Can we keep EBS Managed Attachments after December 2026?',
    a: 'Yes — the EBS Managed Attachments feature continues regardless of WebCenter Content lifecycle dates. The decision is what to do with the WCC tier underneath. Three forward paths exist: upgrade WCC to 14c (preserves everything, 8 years of runway), migrate the WCC file store to OCI Object Storage (preserves the WCC application tier but modernizes storage), or modernize the attachment tier to a cloud-native store while preserving the EBS user experience and integration contract.',
  },
  {
    q: 'What are the steps for migrating Managed Attachments storage to OCI Object Storage?',
    a: 'Migration runs through the WebCenter Content 14c Object Storage Migration Tool, which is built on top of the File Store Provider foundation. Typical sequence: inventory the source file store (volume, age distribution, document-class distribution), provision the OCI Object Storage tier with appropriate lifecycle policies, run the migration tool with metadata preservation, validate parallel retrievability through the EBS Managed Attachments UI, then cut over. EBS Managed Attachments behavior is preserved because EBS still talks to WCC; only the file store backend changes.',
  },
  {
    q: 'What AME framework considerations come up in a Managed Attachments forward path?',
    a: 'The AME rule library is the institutional knowledge that determines where each EBS attachment lands in WCC, so its treatment depends entirely on which forward path is chosen. Operating-unit-specific routing, supplier-segment branching, project-classification folders, and country-specific retention are all encoded in AME rules. Any forward path that preserves the WCC tier (Path 1, Path 2) inherits the AME rules unchanged. A modernization path (Path 3) requires explicit AME rule migration — the rules either re-target the new attachment store or are translated into the new system equivalent.',
  },
  {
    q: 'Does our existing Managed Attachments security model transfer?',
    a: 'Yes — the WCC security model transfers forward on every path, though the mechanism differs. On Path 1 (14c upgrade) and Path 2 (OCI Object Storage migration), the security groups, accounts, role mappings, and document profiles transfer forward unchanged. On Path 3 (modernization), the security model is explicitly migrated and validated against the new attachment store. The integration contract EBS relies on (which user sees which document under which AME-routed folder) is preserved as an explicit migration requirement, not assumed.',
  },
  {
    q: 'What about EBS Forms vs OAF screens for Managed Attachments?',
    a: 'Both EBS Forms-based screens and OAF screens are supported configuration paths for Managed Attachments and both are validated on every forward-path engagement. Forms-based screens are the classic EBS attach interface; OAF screens are the web-based EBS modules. The AME-routing behavior and the WCC retrieval should be identical regardless of which EBS screen the user came from. ADF-based Fusion-style screens are a separate conversation if the customer is mid-migration to Fusion.',
  },
  {
    q: 'How does this interact with our EBS R12.2 or R13 upgrade plans?',
    a: 'EBS Managed Attachments compatibility is an explicit line item on EBS upgrade checklists, and the Managed Attachments forward path is typically sequenced together with the EBS upgrade plan. R12.2 introduced online patching considerations for the Managed Attachments configuration; later EBS releases continue to support Managed Attachments. The inventory phase surfaces dependencies on either side. Andrew has worked the parallel-sequencing problem on multiple Oracle Consulting engagements.',
  },
];

const OracleEbsManagedAttachmentsModernization = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Oracle EBS Managed Attachments Modernization — Migrate to Cloud Storage, Preserve Open Interface | EZ Cloud',
    description:
      'Oracle E-Business Suite Managed Attachments connects EBS to WebCenter Content as the attachment repository. With WebCenter 12c support ending December 2026, EBS admins need a forward path that preserves the Payables Open Interface and the Managed Attachments user experience. EZ Cloud delivers migration to OCI Object Storage or modernization to a cloud-native attachment store — Oracle-supported integration patterns only.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/oracle-ebs-managed-attachments-modernization',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle E-Business Suite' },
      { '@type': 'Thing', name: 'Oracle Managed Attachments' },
      { '@type': 'Thing', name: 'Oracle EBS Managed Attachments' },
      { '@type': 'Thing', name: 'Oracle WebCenter Content' },
      { '@type': 'Thing', name: 'Oracle AME' },
      { '@type': 'Thing', name: 'Approvals Management Engine' },
      { '@type': 'Thing', name: 'OCI Object Storage' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle EBS Managed Attachments Modernization — Migrate to Cloud Storage, Preserve Open Interface | EZ Cloud"
        description="Oracle E-Business Suite Managed Attachments connects EBS to WebCenter Content as the attachment repository. With WebCenter 12c support ending December 2026, EBS admins need a forward path that preserves the Payables Open Interface and the Managed Attachments user experience. EZ Cloud delivers migration to OCI Object Storage or modernization to a cloud-native attachment store — Oracle-supported integration patterns only."
        canonical="https://ezcloud.ai/solutions/oracle-ebs-managed-attachments-modernization"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'EBS Managed Attachments Modernization',
            url: 'https://ezcloud.ai/solutions/oracle-ebs-managed-attachments-modernization',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Paperclip className="w-4 h-4" aria-hidden />
              Oracle EBS · Managed Attachments · WebCenter Content
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle EBS Managed Attachments modernization
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                Preserve the Open Interface. Modernize the storage tier underneath.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              EBS Managed Attachments depends on WebCenter Content. With Fusion Middleware 12c
              support ending December 2026, the attachment tier needs a forward path that
              preserves Oracle EBS integration patterns.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('managed-attachments-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Managed Attachments Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Oracle EBS Managed Attachments, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle EBS Managed Attachments is an E-Business Suite feature that uses Oracle
                  WebCenter Content as the document store
                </strong>{' '}
                for supporting documents attached to EBS transactions — AP invoices, AR receipts,
                Sourcing events, Projects, Purchasing, and many other modules. Users attach
                documents directly from EBS Forms and OAF screens; the actual files live in
                WebCenter Content underneath, with metadata, security profile, and retention
                managed by WCC.
              </p>
              <p>
                The routing logic — which document lands in which WCC folder, under which security
                group, with which retention rule — is driven by the{' '}
                <strong className="text-gray-900">
                  Oracle Approvals Management Engine (AME) framework
                </strong>{' '}
                using EBS context: operating unit, document type, supplier, business unit, project.
                For most EBS shops the AME rule library has accumulated over years and is the
                institutional knowledge underneath the feature.
              </p>
              <p>
                With Oracle Fusion Middleware 12c — including WebCenter Content — exiting Premier
                Support in December 2026, every EBS shop running Managed Attachments has a
                decision in front of it. The EBS feature continues; the WCC tier underneath needs
                a forward path. Three options exist:{' '}
                <Link
                  to="/solutions/webcenter-14c-upgrade"
                  className="text-primary hover:text-accent font-semibold"
                >
                  upgrade WCC to 14c
                </Link>
                , migrate the WCC file store to OCI Object Storage, or modernize the attachment
                tier to a cloud-native store while preserving the EBS user experience and the
                Payables Open Interface.
              </p>
            </div>
          </div>
        </section>

        {/* What EBS Managed Attachments is */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The feature, in detail
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What EBS Managed Attachments actually is
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four facts that frame every forward-path conversation. If you are an EBS admin
              looking at the December 2026 cliff, this is the surface area you are reasoning over.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What's at stake */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What's at stake
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              The December 2026 cliff lands on the storage tier directly
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Every EBS shop running Managed Attachments has WebCenter Content underneath —
                whether the EBS team operates it directly or whether a separate WebCenter team
                does. The December 2026 cliff is on Oracle Fusion Middleware 12c, which is the
                product family WebCenter Content lives in.
              </p>
              <p>
                Premier Support on 12c ends December 2026. Extended Support — security updates and
                critical patches only — ends December 2027. After that, the Oracle-provided
                options are Sustaining Support (limited patching) or Market Driven Support
                (yearly-renewable, costs more than Extended). The EBS Managed Attachments feature
                continues regardless; what changes is the lifecycle posture of the WCC layer the
                feature depends on.
              </p>
              <p>
                For EBS admins, this means the Managed Attachments forward-path conversation is a
                distinct workstream from any EBS upgrade plan. The cross-link is real — EBS R12.2
                and R13 upgrades touch Managed Attachments compatibility as a checklist item — but
                the storage-tier decision is its own decision.
              </p>
              <p>
                For a structured overview of the broader Fusion Middleware 12c decision frame
                across all WebCenter products, see the{' '}
                <Link
                  to="/solutions/webcenter-12c-end-of-support-decision"
                  className="text-primary hover:text-accent font-semibold"
                >
                  WebCenter 12c end-of-support decision page
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Three forward paths */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The forward paths
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Three forward paths for EBS Managed Attachments
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Each preserves the EBS user experience and the integration contract. The differences
              are in what changes underneath, how much modernization happens, and what runway the
              decision buys.
            </p>
            <div className="space-y-5">
              {FORWARD_PATHS.map((p) => (
                <Card key={p.label} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="px-4 py-2 rounded-lg bg-accent/15 text-accent font-bold text-sm uppercase tracking-wide flex-shrink-0">
                      {p.label}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{p.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{p.body}</p>
                      {p.linkTo && p.linkLabel && (
                        <Link
                          to={p.linkTo}
                          className="inline-flex items-center mt-3 font-semibold text-primary hover:text-accent transition-colors text-sm"
                        >
                          {p.linkLabel}
                          <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                        </Link>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What stays the same */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What stays the same
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              In all three forward paths, these are preserved
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              The EBS surface area — what users see, what AME routes, what the integration
              contract relies on — is held constant across every forward path. Modernization
              happens underneath the contract surface, not at it.
            </p>
            <Card className="p-7 bg-accent/5 border border-accent/30 shadow-sm">
              <ul className="space-y-3">
                {PRESERVED.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <ListChecks
                      className="w-5 h-5 text-accent flex-shrink-0 mt-0.5"
                      aria-hidden
                    />
                    <span className="text-gray-800 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </section>

        {/* Why this is a distinct buyer conversation */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Distinct from the AP automation conversation
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Why EBS admins reason about this differently than AP teams
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                The WebCenter AP automation forward-path conversation (AP-throughput, OCR accuracy,
                invoice processing exception handling) is a Finance / AP team conversation. The
                Managed Attachments forward-path conversation is an EBS admin / EBS platform team
                conversation. The buyer, the success criteria, and the integration concerns are
                different.
              </p>
              <p>
                EBS admins care about: does the integration into EBS stay clean? Does the
                Payables Open Interface contract hold? Does the AME framework continue to route
                the same way? Are EBS Forms attach screens and OAF attach screens both still
                supported? Does the storage-tier change introduce any dependency on the next EBS
                upgrade? Is the security model preserved end-to-end?
              </p>
              <p>
                AP throughput, OCR accuracy, and invoice exception workflow are not the EBS
                admin's primary concern on this decision. Those are downstream of an AP automation
                forward path. The Managed Attachments forward path is upstream — the
                document-store decision under all of the EBS modules that use attachments, not
                just AP.
              </p>
              <p>
                For Oracle ERP integration context across the EBS family — Payables Open
                Interface, AME, EBS R12.2 / R13 upgrade considerations — see the{' '}
                <Link
                  to="/integrations/oracle"
                  className="text-primary hover:text-accent font-semibold"
                >
                  Oracle integrations overview
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Common scenarios */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Common scenarios on Managed Attachments forward paths
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four scenarios where EBS admins are working the Managed Attachments forward-path
              decision. Each shapes the engagement scope differently.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for the Managed Attachments decision
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The same 25-year founder-level Oracle WebCenter and EBS expertise that powers EZ
              Cloud's modernization assessments applies directly to EBS Managed Attachments
              engagements. Standard Consulting is the default delivery mode for Tiers 1 through
              3; Forward Deployed Engineering is the right delivery mode for Tier 4 where AME
              archeology and custom Java integration work dominate.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.label} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.label}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link banner */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths, with stack-specific considerations,
              eight diagnostic questions, and a vendor evaluation checklist. Applies to the
              Managed Attachments decision as well as the broader WebCenter AP decision.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on EBS Managed Attachments modernization
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <div className="flex items-center justify-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Users className="w-4 h-4" aria-hidden />
              Founder-led Decision Call
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your Managed Attachments forward path
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud. 25 years across
              the Oracle ecosystem, 20+ years inside Oracle WebCenter — Content (UCM), Imaging
              (IPM), Forms Recognition (WFR), SOA Suite, WebLogic, ADF, and Managed Attachments
              for E-Business Suite. Walk through the EBS modules, the AME rule library, the WCC
              footprint, and the right forward path in the context of your specific
              implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('managed-attachments-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Managed Attachments Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OracleEbsManagedAttachmentsModernization;
